//MapPage with Leaflet and Google Mutant

import React, { useEffect, useState } from "react";
import { MapContainer, Polygon as LeafletPolygon, useMap, ZoomControl, Circle, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.gridlayer.googlemutant";
import useGET from "../services/useGET";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { usePolygon } from "../hooks/usePolygon";
import AreaSidebar from "../components/map/sidebar/AreaSidebar";
import SpeciesList from "../components/map/SpeciesList";
import SpeciesDialog from "../components/map/SpeciesDialog";
import Button from "../components/common/Button";
import { useMapState } from "../hooks/map/useMapState";
import { useUserLocation } from "../hooks/map/useUserLocation";
import { useGoogleLogoPosition } from "../hooks/map/useGoogleLogoPosition";
import { loadGoogleMapsApi } from "../utils/loadGoogleMaps";
import { Link } from "react-router-dom";

// Component to integrate Google Maps tiles in Leaflet
const GoogleMutantLayer = () => {
    const map = useMap();
    const [apiLoaded, setApiLoaded] = useState(false);

    // Load Google Maps API on mount
    useEffect(() => {
        loadGoogleMapsApi()
            .then(() => setApiLoaded(true))
            .catch(err => console.error('Failed to load Google Maps:', err));
    }, []);

    // Reposition Google logo (Reason for this is cause the map controls from leaflet overlaps with the standard logo placement from google)
    useGoogleLogoPosition();

    useEffect(() => {
        if (!map || !apiLoaded) return;

        // Add Google layer once API is loaded
        const googleLayer = L.gridLayer.googleMutant({
            type: "roadmap",
        });
        googleLayer.addTo(map);

        return () => {
            if (googleLayer) {
                map.removeLayer(googleLayer);
            }
        };
    }, [map, apiLoaded]);

    return null;
};

// Component to render a clickable polygon for an area
const AreaPolygon = ({ area, isSelected, onAreaClick }) => {
    if (!area.pins || area.pins.length < 3) return null;

    const positions = area.pins
        .sort((a, b) => a.pin_order - b.pin_order)
        .map(pin => [pin.latitude, pin.longitude]);

    return (
        <LeafletPolygon
            positions={positions}
            pathOptions={{
                color: isSelected ? '#2563eb' : '#6b7280',
                fillColor: isSelected ? '#3b82f6' : '#9ca3af',
                fillOpacity: isSelected ? 0.4 : 0.2,
                weight: isSelected ? 3 : 2,
            }}
            eventHandlers={{
                click: () => onAreaClick(area),
            }}
        />
    );
};

// Component to fly to an area when selected
const FlyToArea = ({ selectedArea, mapCenter, mapZoom }) => {
    const map = useMap();

    useEffect(() => {
        if (!map) return;
        
        if (selectedArea && selectedArea.pins && selectedArea.pins.length >= 3) {
            // Fly to the selected area's bounds
            const positions = selectedArea.pins
                .sort((a, b) => a.pin_order - b.pin_order)
                .map(pin => [pin.latitude, pin.longitude]);
            
            const bounds = L.latLngBounds(positions);
            map.flyToBounds(bounds, {
                padding: [50, 50],
                maxZoom: 13,
                duration: 1,
            });
        } else if (!selectedArea) {
            // Only fly to default center if no area is selected
            map.flyTo(mapCenter, mapZoom, {
                duration: 1,
            });
        }
    }, [selectedArea, map, mapCenter, mapZoom]);

    return null;
};

// Component to get and show user's location
const UserLocation = ({ onLocationFound }) => {
    const { userPosition } = useUserLocation(onLocationFound, 500);

    // Render a circle marker at user's position
    if (!userPosition) return null;

    return (
        <>
            <Circle
                center={userPosition}
                radius={10}
                pathOptions={{
                    color: '#3b82f6',
                    fillColor: '#3b82f6',
                    fillOpacity: 1,
                    weight: 12,
                }}
            >
            </Circle>
        </>
    );
};

export default function AreaSelector() {
    const { data: areas } = useGET(`${process.env.REACT_APP_API_URL}/api/areas`);
    const polygonHook = usePolygon();
    
    const mapState = useMapState(areas, polygonHook);
    const [selectedAreaName, setSelectedAreaName] = useState(null);
    const [selectedAreaData, setSelectedAreaData] = useState(null);
    const [areaSpecies, setAreaSpecies] = useState([]);
    const [userLocationFound, setUserLocationFound] = useState(false);
    const [areasWithPins, setAreasWithPins] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isSpeciesListOpen, setIsSpeciesListOpen] = useState(false);
    const [showSpeciesButton, setShowSpeciesButton] = useState(false);

    // Track window resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch pins for all areas to display polygons
    useEffect(() => {
        if (!areas || areas.length === 0) return;

        const fetchAllAreaPins = async () => {
            try {
                const areasWithPinsData = await Promise.all(
                    areas.map(async (area) => {
                        try {
                            const response = await fetch(
                                `${process.env.REACT_APP_API_URL}/api/pins/${area.id}`
                            );
                            if (response.ok) {
                                const pins = await response.json();
                                return { ...area, pins: pins || [] };
                            }
                            return { ...area, pins: [] };
                        } catch (error) {
                            console.error(`Error fetching pins for area ${area.name}:`, error);
                            return { ...area, pins: [] };
                        }
                    })
                );
                setAreasWithPins(areasWithPinsData);
            } catch (error) {
                console.error("Error fetching all area pins:", error);
            }
        };

        fetchAllAreaPins();
    }, [areas]);

    // Handle area selection (from button or polygon click)
    const handleAreaSelect = (area) => {
        // Reset user location found state to allow flying to the area
        setUserLocationFound(false);
        
        // Find the area with pins from areasWithPins
        const areaWithPins = areasWithPins.find(a => a.id === area.id);
        setSelectedAreaName(area.name);
        setSelectedAreaData(areaWithPins || area);
        
        // Fetch species for the selected area
        fetch(`${process.env.REACT_APP_API_URL}/api/species/${area.id}`)
            .then((response) => {
                if (!response.ok) {
                    console.warn(`No species found for area ${area.id}`);
                    return [];
                }
                return response.json();
            })
            .then((data) => {
                setAreaSpecies(data || []);
                // On mobile, show button if species exist, otherwise hide
                if (isMobile) {
                    setShowSpeciesButton(data && data.length > 0);
                    setIsSpeciesListOpen(false);
                }
            })
            .catch((error) => {
                console.error("Error fetching species:", error);
                setAreaSpecies([]);
                if (isMobile) {
                    setShowSpeciesButton(false);
                    setIsSpeciesListOpen(false);
                }
            });
    };

    // Handle when user location is found
    const handleLocationFound = () => {
        setUserLocationFound(true);
    };

    return (
        <div className="flex flex-col min-h-screen font-body">
            <Navbar />

            {/* Map Container - Full viewport height minus navbar */}
            <div className="relative w-full" style={{ height: 'calc(100vh - 56px)', marginTop: '56px' }}>
                <MapContainer
                    center={[mapState.mapCenter.lat, mapState.mapCenter.lng]}
                    zoom={mapState.mapZoom}
                    style={{ width: "100%", height: "100%", zIndex: 0 }}
                    zoomControl={false}
                    attributionControl={false}
                
                >
                    {/* Google Maps tiles via GoogleMutant */}
                    <GoogleMutantLayer />

                    {/* Zoom control in bottom-right */}
                    <ZoomControl position="bottomright" />

                    {/* Show user's location */}
                    <UserLocation onLocationFound={handleLocationFound} />

                    {/* Fly to selected area or center - only if user location not being shown */}
                    {!userLocationFound && (
                        <FlyToArea 
                            selectedArea={selectedAreaData} 
                            mapCenter={[mapState.mapCenter.lat, mapState.mapCenter.lng]}
                            mapZoom={mapState.mapZoom}
                        />
                    )}

                    {/* Render all area polygons */}
                    {areasWithPins && areasWithPins.map(area => (
                        <AreaPolygon
                            key={area.id}
                            area={area}
                            isSelected={selectedAreaData && selectedAreaData.id === area.id}
                            onAreaClick={handleAreaSelect}
                        />
                    ))}
                </MapContainer>

                {/* Area Sidebar with Search */}
                <AreaSidebar
                    isSidebarOpen={mapState.isSidebarOpen}
                    setIsSidebarOpen={mapState.setIsSidebarOpen}
                    areas={areas}
                    filteredAreas={mapState.filteredAreas}
                    setFilteredAreas={mapState.setFilteredAreas}
                    images={mapState.images}
                    handleView={handleAreaSelect}
                    isSpeciesListOpen={isSpeciesListOpen}
                />

                {/* Species List - Floating on top right */}
                <SpeciesList 
                    areaSpecies={areaSpecies}
                    toggleSpeciesDescription={mapState.toggleSpeciesDescription}
                    isMobile={isMobile}
                    isOpen={isMobile ? isSpeciesListOpen : undefined}
                    onToggleVisibility={isMobile ? (open) => {
                        setIsSpeciesListOpen(open);
                        if (open) setShowSpeciesButton(false);
                        else setShowSpeciesButton(areaSpecies.length > 0);
                    } : undefined}
                />

                {/* Mobile-only Floating Button to Open Species List */}
                {isMobile && showSpeciesButton && areaSpecies.length > 0 && (
                    <div className="absolute w-full z-20 flex justify-center items-center" style={{ bottom: '12%' }}>
                        <Button

                            /*
                            This is how the button is "supposed" to work, rerouted around it a client request since
                            the species info on the map page is ugly.
                                onClick={() => {
                                    setIsSpeciesListOpen(true);
                                    setShowSpeciesButton(false);
                                }}
                            */

                            //This is a fix for rerouting mobile users from map to species with a filter remove once mobile map page specieslist is prettier
                            as={Link}
                            to={`/arter?area=${encodeURIComponent(selectedAreaData.name)}`}
                            //End of above comment

                            variant="primary"
                            className="rounded-full shadow-2xl"
                        >
                            Visa Artlista
                        </Button>
                    </div>
                )}
            </div>

            {/* Footer */}
            <Footer />

            {/* Species Info Dialog */}
            <SpeciesDialog
                isOpen={mapState.dialogOpen}
                species={mapState.selectedSpecies}
                onClose={mapState.handleDialogClose}
            />
        </div>
    );
}
